import { inject, Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import {
  catchError,
  EMPTY,
  from,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
} from 'rxjs'
import {
  deleteConversation,
  hydrateAssistantResult,
  loadConversation,
  loadConversationFailure,
  loadConversations,
  loadConversationsSuccess,
  loadConversationSuccess,
} from '../../../actions/analytics-copilot.actions'
import { CopilotQueryStatus } from '../models/copilot.model'
import { CopilotRealtimeService } from '../services/copilot-realtime.service'
import { CopilotService } from '../services/copilot.service'

@Injectable({ providedIn: 'root' })
export class CopilotConversationEffects {
  readonly #actions$ = inject(Actions)
  readonly #copilotService = inject(CopilotService)
  readonly #realtimeService = inject(CopilotRealtimeService)
  readonly #router = inject(Router)

  loadConversations$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(loadConversations),
      switchMap(({ userId }) =>
        this.#copilotService.listConversations().pipe(
          map(conversations =>
            loadConversationsSuccess({ userId, conversations })
          ),
          catchError(() => EMPTY)
        )
      )
    )
  )

  loadConversation$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(loadConversation),
      switchMap(({ userId, id }) =>
        this.#copilotService.getConversation(id).pipe(
          map(conversation =>
            loadConversationSuccess({ userId, conversation })
          ),
          catchError(() => of(loadConversationFailure({ id })))
        )
      )
    )
  )

  redirectOnConversationNotFound$ = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(loadConversationFailure),
        tap(() => this.#router.navigate(['/analytics/copilot']))
      ),
    { dispatch: false }
  )

  rehydratePending$ = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(loadConversationSuccess),
        tap(({ userId, conversation }) => {
          const hasProcessing = conversation.messages.some(
            m => m.status === CopilotQueryStatus.Processing
          )
          if (hasProcessing) {
            this.#realtimeService.rehydratePending({ userId, conversation })
          }
        })
      ),
    { dispatch: false }
  )

  clearRealtimeOnDelete$ = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(deleteConversation),
        tap(({ conversationId }) => this.#realtimeService.clear(conversationId))
      ),
    { dispatch: false }
  )

  hydrateResults$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(loadConversationSuccess),
      mergeMap(({ userId, conversation }) => {
        const completedMessages = conversation.messages.filter(
          m => m.status === CopilotQueryStatus.Completed
        )
        return from(completedMessages).pipe(
          mergeMap(m =>
            this.#copilotService.getMessageResult(conversation.id, m.id).pipe(
              map(apiResult =>
                hydrateAssistantResult({
                  userId,
                  conversationId: conversation.id,
                  messageId: m.id,
                  result: {
                    type: apiResult.result_type,
                    content: apiResult.content,
                  },
                })
              ),
              catchError(() => EMPTY)
            )
          )
        )
      })
    )
  )
}
